import React, { useEffect, useState } from 'react';
import { Search, MapPin } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import CustomSelect from './CustomSelect';

const HeroSection = ({ onSearch, categories = [] }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState('');
    const [showFilter, setShowFilter] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchCities = async () => {
            try {
                const response = await api.get('/cities');
                const list = response.data || [];
                setCities([{ _id: 'all', name: 'All Cities' }, ...list]);
            } catch (error) {
                console.error('Error fetching cities:', error);
            }
        };
        fetchCities();
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        const trimmed = searchTerm.trim();
        if (trimmed || selectedCity) {
            if (onSearch) {
                onSearch(trimmed, selectedCity);
            } else {
                const params = new URLSearchParams();
                if (trimmed) {
                    params.set('q', trimmed);
                }
                if (selectedCity && selectedCity !== 'all') {
                    params.set('city', selectedCity);
                } else if (selectedCity === 'all') {
                    params.set('city', 'all');
                }
                navigate(`/search?${params.toString()}`);
            }
        }
    };

    const handleCityChange = (e) => {
        setSelectedCity(e.target.value);
    };

    const quickCategories = categories.slice(0, 6);

    return (
        <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 dark:from-primary-900 dark:via-primary-950 dark:to-gray-950">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}></div>
            </div>

            <div className="container-custom relative z-10 py-16 md:py-24 lg:py-32">
                {/* Hero Content */}
                <div className="text-center max-w-4xl mx-auto">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 animate-fade-in">
                        Discover Local Businesses in <span className="text-yellow-400">Fatehabad</span>
                    </h1>
                    <p className="text-lg md:text-xl text-blue-100 mb-8 md:mb-12 animate-slide-up">
                        Your trusted directory for finding the best local services, shops, and restaurants
                    </p>

                    {/* Search Bar */}
                    <form onSubmit={handleSearch} className="max-w-3xl mx-auto mb-10 animate-slide-up relative z-40" style={{ animationDelay: '0.1s' }}>
                        <div className="relative bg-white/10 backdrop-blur-md border border-white/20 p-2 rounded-3xl md:rounded-full flex flex-col md:flex-row gap-2 items-center shadow-2xl transition-all duration-300 hover:bg-white/15">

                            {/* Search Input Group */}
                            <div className="flex-1 flex items-center px-6 py-3 w-full">
                                <Search className="h-5 w-5 text-blue-200 mr-3 flex-shrink-0" />
                                <input
                                    type="text"
                                    placeholder="Search businesses, services, products..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full bg-transparent border-none focus:outline-none focus:ring-0 text-white placeholder-blue-200/50 text-base md:text-lg"
                                    style={{ boxShadow: 'none', outline: 'none', border: 'none' }}
                                />
                                <div className="h-6 w-px bg-white/20 mx-4 hidden md:block" />
                                <button
                                    type="button"
                                    onClick={() => setShowFilter(!showFilter)}
                                    className={`p-2 rounded-full transition-all duration-200 ${showFilter
                                        ? 'bg-white/20 text-white shadow-inner'
                                        : 'text-blue-200 hover:bg-white/10 hover:text-white'
                                        }`}
                                    title="Filter by City"
                                >
                                    <LucideIcons.SlidersHorizontal className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Location Selector - Animated Reveal (Desktop Only) */}
                            {showFilter && (
                                <div className="hidden md:block md:w-64 animate-fade-in">
                                    <div className="pr-2">
                                        <CustomSelect
                                            name="city"
                                            value={selectedCity}
                                            onChange={handleCityChange}
                                            options={cities}
                                            placeholder="Select City"
                                            icon={MapPin}
                                            className="w-full bg-white/5 border-white/10 text-white placeholder-blue-200/50 backdrop-blur-sm focus:border-white/30 rounded-xl"
                                            dropdownClassName="bg-gray-900 border border-gray-700 text-white"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Mobile Filter View */}
                            {showFilter && (
                                <div className="w-full md:hidden px-2 pb-2 animate-fade-in relative z-50">
                                    <CustomSelect
                                        name="city"
                                        value={selectedCity}
                                        onChange={handleCityChange}
                                        options={cities}
                                        placeholder="Select City"
                                        icon={MapPin}
                                        className="w-full bg-white/5 border-white/10 text-white placeholder-blue-200/50 rounded-xl"
                                        dropdownClassName="bg-gray-900 border border-gray-700 text-white"
                                    />
                                </div>
                            )}

                            {/* Search Button */}
                            <button
                                type="submit"
                                className="w-full md:w-auto px-8 py-3.5 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-300 hover:to-yellow-400 text-gray-900 font-bold rounded-2xl md:rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap"
                            >
                                Search
                            </button>
                        </div>
                    </form>

                    {/* Quick Category Pills */}
                    {quickCategories.length > 0 && (
                        <div className="flex flex-wrap justify-center gap-3 animate-slide-up max-w-4xl mx-auto relative z-10" style={{ animationDelay: '0.2s' }}>
                            {quickCategories.map((category) => {
                                const IconComponent = category.icon ? (LucideIcons[category.icon] || LucideIcons.Folder) : LucideIcons.Folder;
                                return (
                                    <button
                                        key={category._id}
                                        onClick={() => navigate(`/${category.slug}`)}
                                        className="group px-5 py-2.5 bg-white/5 hover:bg-white/15 backdrop-blur-md rounded-full text-sm font-medium text-white transition-all duration-300 border border-white/10 hover:border-white/30 flex items-center gap-2 hover:-translate-y-0.5"
                                    >
                                        <IconComponent className="w-4 h-4 text-blue-300 group-hover:text-yellow-400 transition-colors" />
                                        {category.name}
                                    </button>
                                );
                            })}
                            <button
                                onClick={() => navigate('/categories')}
                                className="group px-5 py-2.5 bg-yellow-400/20 hover:bg-yellow-400/30 text-yellow-300 hover:text-yellow-200 backdrop-blur-md rounded-full text-sm font-semibold transition-all duration-300 border border-yellow-400/40 hover:border-yellow-400/60 flex items-center gap-1.5 hover:-translate-y-0.5"
                            >
                                <span>All Categories</span>
                                <span className="text-xs">&rarr;</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Wave Divider */}
            <div className="absolute bottom-0 left-0 right-0">
                <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
                    <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="currentColor" className="text-gray-50 dark:text-gray-950" />
                </svg>
            </div>
        </div>
    );
};

export default HeroSection;
